import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AssistResourceBM, CalendarSchedulingSettings, EventStatusResponse, fromLocalized } from '@cue/api';
import {
  CalendarAttendee,
  CalendarAvailability,
  CalendarEvent,
  CalendarEventDatetime,
  CalendarLocation,
  CalendarStatus,
  CalendarWorkingHour,
  getCalendarWorkingHoursIntervalsForRange,
} from '@cue/calendars';
import { ConfigService } from '../services';

import {
  Filter,
  FilterAttendee,
  FilterBase,
  getResourceAttendeeStatusAndConfirmationMetadata,
  IdentifiedAssistEvent,
  Requirement,
  Schedule,
  TemporalResourceAttendee,
} from '../models';
import { add, addDays, addMinutes, differenceInMinutes, endOfDay, set, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { scheduleHelper } from './schedule-helper';
import { LicensingService } from '@cue/licensing';
import { remove } from 'remove-accents';
import { localized } from '@cue/translate';
import { ResourceAttendee } from '../models/app/resource-attendee.model';
import { distinctBy } from './array.utils';
import { translation } from '@cue/translations';
import { getTimeZoneFromOfficeString, WorkingHoursModel } from '@cue/data';
import { ResourceAttendeeStatus } from './constants';
import { getResourceTimeZone } from './resource-timezone.utils';

export function getIndexFromDate(start: Date, date: Date): number {
  const index = Math.ceil(differenceInMinutes(date, start, { roundingMethod: 'floor' }) / 15);
  return index;
}

export function isAllDayEvent(start: Date, end: Date) {
  return (
    start.getTime() == startOfDay(start).getTime() &&
    (set(end.getTime(), { seconds: 0, milliseconds: 0 }).getTime() ==
      set(endOfDay(end), {
        seconds: 0,
        milliseconds: 0,
      }).getTime() ||
      end.getTime() == endOfDay(end).getTime() ||
      set(startOfDay(end), { milliseconds: 0, seconds: 0 }).getTime() ==
        set(end, {
          seconds: 0,
          milliseconds: 0,
        }).getTime())
  );
}

export function isDifferent(previousValue: any, nextValue: any) {
  const previousFreeOrFreeWorkingHours =
    previousValue === CalendarAvailability.free || previousValue === CalendarAvailability.freeOutsideWorkingHours;
  const nextFreeOrFreeWorkingHours =
    nextValue === CalendarAvailability.free || nextValue === CalendarAvailability.freeOutsideWorkingHours;
  if (previousFreeOrFreeWorkingHours && nextFreeOrFreeWorkingHours) {
    return false;
  }
  return previousValue !== nextValue;
}

export function getDateFromIndex(start: Date, index: number): Date {
  const date = addMinutes(start, index * 15);
  return date;
}

export function patchAvaibalityFromTePast(now: Date, start: Date, availabilityView: string | CalendarAvailability[]) {
  const data: CalendarAvailability[] = Array.isArray(availabilityView)
    ? availabilityView
    : (availabilityView.split('') as CalendarAvailability[]);
  return data.map((value, index) => (getDateFromIndex(start, index + 1) < now ? CalendarAvailability.limited : value));
}

export function patchAvailabilityByRestrictions(
  resourceTimezone: string,
  startDate: Date,
  now: Date,
  availabilityView: string | CalendarAvailability[],
  schedulingSettings?: CalendarSchedulingSettings,
  restrictedTo?: 'you' | 'other'
): CalendarAvailability[] {
  const data: CalendarAvailability[] = Array.isArray(availabilityView)
    ? availabilityView
    : (availabilityView.split('') as CalendarAvailability[]);

  if (restrictedTo == 'you') {
    return data.map((bit) => (bit == CalendarAvailability.free ? CalendarAvailability.free : bit));
  }

  if (restrictedTo == 'other') {
    return data.map((bit) => (bit == CalendarAvailability.free ? CalendarAvailability.free : bit));
  }

  if (schedulingSettings?.maxAdvanceDays != null) {
    const timezone = getTimeZoneFromOfficeString(resourceTimezone);
    const timeInZone = toZonedTime(addDays(now, schedulingSettings.maxAdvanceDays), timezone);
    const endDate = endOfDay(timeInZone);

    const maxIndex = getIndexFromDate(startDate, endDate) - 1;
    return data.map((bit, index) => (index > maxIndex ? CalendarAvailability.limited : bit));
  }

  return data;
}

export function mergeAttendeesAvailability(
  attendeesAvailabilities: FilterAttendee[],
  start: Date,
  end: Date
): CalendarAvailability[] {
  const availabilities = attendeesAvailabilities.map((x) => x.availabilityView.split(''));
  const final = generateAvailability(start, end, CalendarAvailability.free);
  for (let i = 0; i < final.length; i++) {
    for (let j = 0; j < attendeesAvailabilities.length; j++) {
      if (
        availabilities[j][i] !== CalendarAvailability.free &&
        availabilities[j][i] !== CalendarAvailability.tentative
      ) {
        final[i] = CalendarAvailability.reserved;
        break;
      }
    }
  }
  return final;
}

export function daysBetweemDates(fromDate: Date, toDate: Date): Date[] {
  const days: Date[] = [];

  let firstDay = fromDate;
  while (true) {
    // Add day with hours
    days.push(firstDay);

    firstDay = add(firstDay, { days: 1 });
    if (firstDay > toDate) break;
  }
  return days;
}

export function patchSchedules(
  schedules: Schedule[],
  filter: Filter,
  resources: AssistResourceBM[],
  loadedAttendees: FilterAttendee[]
): Schedule[] {
  return schedules.map((schedule) => {
    const attendees = filter.attendees;
    const requirementAttendeeEmails = attendees.filter((x) => !x.optional).map((x) => (x.value ?? '').toUpperCase());
    const requiredAttendees = loadedAttendees.filter((la) =>
      requirementAttendeeEmails.includes(la.email.toUpperCase())
    );
    const resource = resources.find((x) => x.id == schedule.resourceId)!;
    const resourceTimeZone = getResourceTimeZone(resource.workingHours);
    const reservedTo = resource.conditions.reservedTo;

    let computedCalendarAvailability = schedule.availabilityView.split('') as CalendarAvailability[];
    computedCalendarAvailability = scheduleHelper.patchAvailabilityByRestrictions(
      resourceTimeZone,
      startOfDay(filter.date),
      computedCalendarAvailability,
      schedule.schedulingSettings,
      reservedTo
    );

    computedCalendarAvailability = scheduleHelper.patchAvailabilityViewByWorkingHoursAndLimits(
      computedCalendarAvailability,
      resource.workingHours,
      resourceTimeZone,
      filter,
      schedule.schedulingSettings
    );

    computedCalendarAvailability = scheduleHelper.patchAvailabilityViewByLimits(computedCalendarAvailability, filter);

    const patchedByAttendees = patchAvailabilityByAttendees(
      startOfDay(filter.date),
      endOfDay(filter.date),
      computedCalendarAvailability,
      requiredAttendees
    );
    return {
      ...schedule,
      availabilityView: patchedByAttendees.join(''),
    } as Schedule;
  });
}

export function patchAvailabilityByAttendees(
  start: Date,
  end: Date,
  availabilityView: string | CalendarAvailability[],
  attendeesAvailabilities: FilterAttendee[]
) {
  const data: CalendarAvailability[] = Array.isArray(availabilityView)
    ? availabilityView
    : (availabilityView.split('') as CalendarAvailability[]);
  const attendesAvailabiliies: CalendarAvailability[] = mergeAttendeesAvailability(attendeesAvailabilities, start, end);

  const final = generateAvailability(start, end, CalendarAvailability.free);
  for (let i = 0; i < final.length; i++) {
    final[i] = data[i];
    if (attendesAvailabiliies[i] !== CalendarAvailability.free) {
      final[i] = CalendarAvailability.limited;
    }
  }

  return final;
}

export function patchAvailabilityByWorkingHours(
  start: Date,
  availabilityView: string | CalendarAvailability[],
  wHours: CalendarWorkingHour[],
  resourceTimeZoneName: string,
  schedulingSettings?: CalendarSchedulingSettings
) {
  const data: CalendarAvailability[] = Array.isArray(availabilityView)
    ? availabilityView
    : (availabilityView.split('') as CalendarAvailability[]);
  const resourceTimeZone = getTimeZoneFromOfficeString(resourceTimeZoneName) ?? resourceTimeZoneName;
  const intervals = getCalendarWorkingHoursIntervalsForRange({
    workingHours: wHours,
    timeZone: resourceTimeZone,
    rangeStart: start,
    rangeEnd: getDateFromIndex(start, data.length),
  });

  const result = data.map((value, index) => {
    if (value !== CalendarAvailability.free) {
      return value;
    }
    const slotStart = getDateFromIndex(start, index);
    const anySlot = intervals.some((interval) => interval.start <= slotStart && slotStart < interval.end);

    return anySlot
      ? value
      : schedulingSettings?.rejectOutsideWorkingHours
        ? CalendarAvailability.closed
        : CalendarAvailability.freeOutsideWorkingHours;
  });

  return result;
}

export function extractMaxAvailabilityWithin(
  availabilityView: CalendarAvailability[],
  start: Date,
  origin: Date
): { leftIndex: number; rightIndex: number; leftDate: Date; rightDate: Date } {
  const originIndex = getIndexFromDate(start, origin);
  let leftIndex = originIndex;
  let rightIndex = originIndex;

  while (
    availabilityView[leftIndex - 1] === CalendarAvailability.free ||
    availabilityView[leftIndex - 1] === CalendarAvailability.freeOutsideWorkingHours
  ) {
    leftIndex--;
  }

  while (
    availabilityView[rightIndex + 1] === CalendarAvailability.free ||
    availabilityView[rightIndex + 1] === CalendarAvailability.freeOutsideWorkingHours
  ) {
    rightIndex++;
  }
  rightIndex = rightIndex + 1;

  const leftDate = getDateFromIndex(start, leftIndex);
  const rightDate = getDateFromIndex(start, rightIndex);

  return {
    leftDate,
    leftIndex,
    rightIndex,
    rightDate,
  };
}

export function getPatchedAvailabilityWithEvent(
  availabilityView: CalendarAvailability[],
  start: Date,
  eventStart: Date,
  eventEnd: Date
) {
  return availabilityView.map((value, index) => {
    const slotStart = getDateFromIndex(start, index);
    const slotEnd = addMinutes(slotStart, 15);
    const hasOverlap = slotStart < eventEnd && slotEnd > eventStart;

    if (hasOverlap) {
      return value === CalendarAvailability.freeOutsideWorkingHours
        ? CalendarAvailability.freeOutsideWorkingHours
        : CalendarAvailability.free;
    } else {
      return value;
    }
  });
}

export function generateAvailability(from: Date, to: Date, availability: CalendarAvailability) {
  const fixedFrom = from < to ? from : to;
  const fixedTo = to > from ? to : from;
  const index = Math.ceil(differenceInMinutes(fixedTo, fixedFrom, { roundingMethod: 'ceil' }) / 15);
  return new Array(index).fill(availability);
}

export function getAvailabilityFromView(availabilityView: CalendarAvailability[], from: Date, target: Date) {
  const index = differenceInMinutes(target, from, { roundingMethod: 'ceil' }) / 15;
  return availabilityView[index];
}

export function getSubAvailabilityFromAvailability(
  availabiltyviView: CalendarAvailability[],
  originalFrom: Date,
  from: Date,
  to: Date
) {
  const startIndex = Math.floor(differenceInMinutes(from, originalFrom, { roundingMethod: 'ceil' }) / 15);
  const endIndex = Math.floor(differenceInMinutes(to, originalFrom, { roundingMethod: 'ceil' }) / 15);
  return availabiltyviView.slice(startIndex, endIndex);
}

export function getTotallAvailability(availabilityView: CalendarAvailability[]): CalendarAvailability {
  if (availabilityView.find((x) => x == CalendarAvailability.closed)) {
    return CalendarAvailability.closed;
  }
  if (availabilityView.find((x) => x == CalendarAvailability.reserved)) {
    return CalendarAvailability.reserved;
  }
  if (availabilityView.find((x) => x == CalendarAvailability.limited)) {
    return CalendarAvailability.limited;
  }
  if (availabilityView.find((x) => x == CalendarAvailability.assignedToOthers)) {
    return CalendarAvailability.assignedToOthers;
  }
  if (availabilityView.find((x) => x == CalendarAvailability.tentative)) {
    return CalendarAvailability.tentative;
  }
  return CalendarAvailability.free;
}

/**
 * Check if the resource attendee has accepted the invitation
 * Resource accepted the invitation if the status is 'accepted' or 'Accept'.
 * If the status is 'declined' or 'Decline', it means the resource declined the invitation.
 * If the status is neither, it is better to say false.
 * @param resourceAttendee
 */
export function resourceAttendeeAccepted(resourceAttendee: TemporalResourceAttendee): boolean | null {
  const accepted =
    resourceAttendee.attendee.status.response === 'accepted' || resourceAttendee.attendee.status.response === 'Accept'
      ? true
      : resourceAttendee.attendee.status.response === 'declined' ||
          resourceAttendee.attendee.status.response === 'Decline'
        ? false
        : null;

  return accepted;
}

export function findResourceAttendeeResponse(
  resourceAttendee: TemporalResourceAttendee,
  icalUid: string,
  eventId: string,
  responses: EventStatusResponse[]
): EventStatusResponse | null;

export function findResourceAttendeeResponse(
  resourceAttendee: string,
  icalUid: string,
  eventId: string,
  responses: EventStatusResponse[]
): EventStatusResponse | null;

export function findResourceAttendeeResponse(
  emailOrResourceAttendee: string | TemporalResourceAttendee,
  icalUid: string,
  eventId: string,
  responses: EventStatusResponse[]
): EventStatusResponse | null {
  let email = '';
  if (typeof emailOrResourceAttendee === 'string') {
    email = emailOrResourceAttendee;
  } else {
    email = emailOrResourceAttendee.attendee.emailAddress.address;
  }
  const foundResponse = responses.find((response) => {
    if (response.email.toUpperCase() !== email.toUpperCase()) {
      return false;
    }

    if (response.iCalUid !== icalUid) {
      return false;
    }

    if (response.id && eventId) {
      return response.id === eventId;
    }
    return false;
  });
  if (foundResponse) {
    return foundResponse;
  } else {
    return null;
  }
}

export function resourceAttendeeCanceled(
  icalUid: string,
  eventId: string,
  resourceAttendee: ResourceAttendee,
  responses: EventStatusResponse[]
) {
  const response = findResourceAttendeeResponse(
    resourceAttendee.attendee.emailAddress.address,
    icalUid,
    eventId,
    responses
  );
  if (response) {
    return response.canceled;
  }
  return false;
}

export function resourceAttendeeConfirmed(
  icalUid: string,
  eventId: string,
  resourceAttendee: ResourceAttendee,
  responses: EventStatusResponse[]
) {
  const response = findResourceAttendeeResponse(
    resourceAttendee.attendee.emailAddress.address,
    icalUid,
    eventId,
    responses
  );
  if (response) {
    return response.confirmed;
  }
  return false;
}

export function prepareEventForDetail(
  translocoService: TranslocoService,
  resource: AssistResourceBM,
  translation: any,
  translocoPipe: TranslocoPipe,
  configService: ConfigService,
  status: { key: string; value: string } | null,
  start?: Date,
  end?: Date,
  requirementId?: string
) {
  let subject = getDefaultReservationSubject(configService, translocoService, resource.resourceTypeId);

  let description = translocoPipe.transform(translation.resourceDetail.pleaseJoinMe);

  if (configService.value.reservationDefaults.descriptionText) {
    description = configService.value.reservationDefaults.descriptionText as any;
  }

  const defaultDescription = configService.value.resourceTypeInfos.find(
    (x) => x.resourceTypeId == resource.resourceTypeId
  )?.customDescription;

  if (localized(defaultDescription, translocoService)) {
    description = localized(defaultDescription, translocoService);
  }

  return {
    start: start ? CalendarEventDatetime.fromBrowserDatetime(start) : null,
    end: end ? CalendarEventDatetime.fromBrowserDatetime(end) : null,
    subject: subject,
    status: status,
    bodyPreview: description,
    bodyHtml: description,
    resourceAttendees: [
      {
        resource: resource,
        attendee: {
          resource: true,
          required: true,
          type: 'resource',
          emailAddress: resource.email,
          status: 'tentative',
        },
        statusesByDay: [],
      },
    ],
    requirementId: requirementId,
    visibility: 'public',
    type: 'singleInstance',
    recurrence: null,
  };
}

export function getDefaultReservationSubject(
  configService: ConfigService,
  translocoService: TranslocoService,
  resourceTypeId?: number | null
): string {
  let subject = translocoService.translate(translation.resourceDetail.defaultTitle);

  if (configService.value.reservationDefaults.subjectText) {
    subject = configService.value.reservationDefaults.subjectText as string;
  }

  if (resourceTypeId == null) {
    return subject;
  }

  const customTitle = configService.value.resourceTypeInfos.find(
    (x) => x.resourceTypeId == resourceTypeId
  )?.customTitle;
  return localized(customTitle, translocoService) || subject;
}

export function findResourceFromEvent(
  event: CalendarEvent,
  resources: AssistResourceBM[],
  activeLanguage: string,
  defaultLanguage: string
) {
  const resourcesInAttendees = event.attendees ? event.attendees.filter((x) => x.resource === true) : [];
  if (resourcesInAttendees.length === 1) {
    return resources.find(
      (x) =>
        x.username?.toUpperCase() === resourcesInAttendees[0].emailAddress.address?.toUpperCase() ||
        x.username?.toUpperCase() === resourcesInAttendees[0].emailAddress.name?.toUpperCase() ||
        x.username.toUpperCase() === resourcesInAttendees[0].emailAddress.address?.toUpperCase() ||
        x.name?.toUpperCase() === resourcesInAttendees[0].emailAddress.address?.toUpperCase() ||
        x.name?.toUpperCase() === resourcesInAttendees[0].emailAddress.name?.toUpperCase() ||
        x.email.toUpperCase() === resourcesInAttendees[0].emailAddress.address?.toUpperCase()
    );
  }

  const foundFromLocation = resources.find(
    (x) =>
      event.locations.find(
        (location) =>
          x.username?.toUpperCase() === location.uniqueId?.toUpperCase() ||
          x.username?.toUpperCase() === location.displayName?.toUpperCase() ||
          x.username.toUpperCase() === location.uniqueId?.toUpperCase() ||
          x.name?.toUpperCase() === location.uniqueId?.toUpperCase() ||
          x.name?.toUpperCase() === location.displayName?.toUpperCase() ||
          x.email.toUpperCase() === location.uniqueId?.toUpperCase()
      ) != null
  );

  if (foundFromLocation) {
    return foundFromLocation;
  } else {
    return resources.find((resource) => {
      return (
        event.attendees.find(
          (attendee) =>
            attendee.emailAddress?.address?.toUpperCase() === resource.email?.toUpperCase() ||
            attendee.emailAddress?.address?.toUpperCase() === resource.username?.toUpperCase() ||
            attendee.emailAddress?.address?.toUpperCase() ===
              fromLocalized(resource.displayName, activeLanguage, defaultLanguage)?.toUpperCase() ||
            attendee.emailAddress?.name?.toUpperCase() === resource.email?.toUpperCase() ||
            attendee.emailAddress?.name?.toUpperCase() === resource.username?.toUpperCase() ||
            attendee.emailAddress?.name?.toUpperCase() ===
              fromLocalized(resource.displayName, activeLanguage, defaultLanguage)?.toUpperCase()
        ) != null
      );
    });
  }
}

export function findResourceFromUniqueIdAndDisplayName(
  uniqueId: string,
  displayName: string,
  resources: AssistResourceBM[]
): AssistResourceBM {
  return resources.find(
    (x) =>
      x.username?.toUpperCase() === uniqueId?.toUpperCase() ||
      x.username?.toUpperCase() === displayName?.toUpperCase() ||
      x.name?.toUpperCase() === uniqueId?.toUpperCase() ||
      x.name?.toUpperCase() === displayName?.toUpperCase() ||
      x.email.toUpperCase() === displayName?.toUpperCase() ||
      x.email.toUpperCase() === uniqueId?.toUpperCase()
  )!;
}

export function getImageCoverFromResource(resource: AssistResourceBM) {
  return resource && resource.files.length > 0 ? resource.files[0].imagePreferCover : false;
}

export function getImageUrlFromResource(resource: AssistResourceBM) {
  return resource && resource.files.length > 0 ? resource.files[0].file : '';
}

export function tryToFindLocation(
  item: any,
  resources: AssistResourceBM[],
  activeLang: string,
  defaultLang: string
): CalendarLocation {
  try {
    if (resources.length === 0) {
      return null;
    }

    if (item.location != null) {
      const splits = item.location.trim().split(',');
      const mappedFromSplits = splits
        .map((x: any) => findResourceFromUniqueIdAndDisplayName(x, x, resources))
        .filter((x: any) => x != null);
      if (mappedFromSplits.length > 0) {
        return {
          uniqueId: mappedFromSplits[0].email,
          displayName: mappedFromSplits[0].name,
          locationType: 'N/A',
          locationUri: 'N/A',
          uniqueIdType: 'N/A',
        };
      }
    } else {
      const resourcesInAttendees = item.attendees
        ? item.attendees.filter((x: CalendarAttendee) => x.resource === true)
        : [];
      if (resourcesInAttendees.length === 1) {
        const foundFromAttendees = resources.find(
          (x) =>
            x.username?.toUpperCase() === resourcesInAttendees[0].email?.toUpperCase() ||
            x.username?.toUpperCase() ===
              fromLocalized(resourcesInAttendees[0].displayName, activeLang, defaultLang)?.toUpperCase() ||
            x.username?.toUpperCase() === resourcesInAttendees[0].emailAddress?.address?.toUpperCase() ||
            x.username?.toUpperCase() === resourcesInAttendees[0].emailAddress?.name?.toUpperCase() ||
            x.name?.toUpperCase() === resourcesInAttendees[0].email?.toUpperCase() ||
            x.name?.toUpperCase() ===
              fromLocalized(resourcesInAttendees[0].displayName, activeLang, defaultLang)?.toUpperCase() ||
            x.name?.toUpperCase() === resourcesInAttendees[0].emailAddress?.address?.toUpperCase() ||
            x.name?.toUpperCase() === resourcesInAttendees[0].emailAddress?.name?.toUpperCase() ||
            x.email?.toUpperCase() === resourcesInAttendees[0].email?.toUpperCase() ||
            x.email.toUpperCase() === resourcesInAttendees[0].emailAddress?.address?.toUpperCase()
        );
        if (!foundFromAttendees) {
          return null;
        }
        return {
          uniqueId: foundFromAttendees!.email,
          displayName: foundFromAttendees!.name,
          locationType: 'N/A',
          locationUri: 'N/A',
          uniqueIdType: 'N/A',
        };
      }

      return null;
    }
  } catch (e) {
    console.warn('Cannot find the location in the event');
    console.warn(e);
    return null;
  }
  return null;
}

export function noResponse(status: CalendarStatus) {
  return (
    status.response === 'NoResponseReceived' ||
    status.response === 'None' ||
    status.response === 'notResponded' ||
    status.response === 'none' ||
    status.response === 'tentative' ||
    status.response === 'Tentative'
  );
}

export function getTemporalResourceAttendees(
  event: CalendarEvent,
  resources: AssistResourceBM[],
  activeLanguage: string,
  defaultLanguage: string
): TemporalResourceAttendee[] {
  const finalArray: TemporalResourceAttendee[] = [];

  // lokace a attendee parovani - idealni situace
  const locationAttendeeStategy = event.attendees
    .map((x) => {
      try {
        const locationForAttendee = event.locations.find(
          (location) =>
            (x.emailAddress.address != null &&
              x.emailAddress.address.toUpperCase() === location.locationUri?.toUpperCase()) ||
            (x.emailAddress.name != null &&
              x.emailAddress.name?.toUpperCase() === location.displayName?.toUpperCase()) ||
            (location.uniqueId != null && x.emailAddress.address.toUpperCase() === location.uniqueId?.toUpperCase()) ||
            (x.emailAddress.name != null &&
              x.emailAddress.name.toUpperCase() === location.locationUri?.toUpperCase()) ||
            (x.emailAddress.address != null &&
              x.emailAddress.address.toUpperCase() === location.locationUri?.toUpperCase()) ||
            (x.emailAddress.name != null &&
              x.emailAddress.name?.toUpperCase() === location.displayName?.toUpperCase()) ||
            (x.emailAddress.address != null &&
              location.displayName != null &&
              x.emailAddress.address?.toUpperCase() === location.displayName?.toUpperCase())
        );
        if (locationForAttendee) {
          const foundResource = findResourceFromUniqueIdAndDisplayName(
            x.emailAddress.address?.toUpperCase(),
            x.emailAddress.name?.toUpperCase(),
            resources
          );
          if (foundResource) {
            return {
              attendee: x,
              resource: foundResource,
              location: locationForAttendee,
            };
          } else {
            return null;
          }
        } else {
          return null;
        }
      } catch (ex) {
        console.warn('Malformed  response - attendee');
        console.warn(ex);
        console.table(JSON.stringify(event, null, 2));
        return null;
      }
    })
    .filter((x) => x !== null);

  const locationResourceStrategy = resources
    .map((r) => {
      const location = event.locations.find(
        (location) =>
          r.username.toUpperCase() === location.locationUri?.toUpperCase() ||
          r.username.toUpperCase() === location.displayName?.toUpperCase() ||
          r.username.toUpperCase() === location.uniqueId?.toUpperCase() ||
          r.email.toUpperCase() === location.locationUri?.toUpperCase() ||
          r.email.toUpperCase() === location.displayName?.toUpperCase() ||
          r.email.toUpperCase() === location.uniqueId?.toUpperCase() ||
          fromLocalized(r.displayName, activeLanguage, defaultLanguage).toUpperCase() ===
            location.locationUri?.toUpperCase() ||
          fromLocalized(r.displayName, activeLanguage, defaultLanguage).toUpperCase() ===
            location.displayName?.toUpperCase() ||
          fromLocalized(r.displayName, activeLanguage, defaultLanguage).toUpperCase() ===
            location.uniqueId?.toUpperCase()
      );

      if (location) {
        const foundAttendeeFromFoundResource = event.attendees.find((x) => {
          try {
            return (
              x.emailAddress?.address.toUpperCase() == r.email.toUpperCase() ||
              x.emailAddress?.address.toUpperCase() == r.username.toUpperCase() ||
              x.emailAddress?.address.toUpperCase() ==
                fromLocalized(r.displayName, activeLanguage, defaultLanguage).toUpperCase() ||
              x.emailAddress?.name.toUpperCase() == r.email.toUpperCase() ||
              x.emailAddress?.name.toUpperCase() == r.username.toUpperCase() ||
              x.emailAddress?.name.toUpperCase() ==
                fromLocalized(r.displayName, activeLanguage, defaultLanguage).toUpperCase()
            );
          } catch (ex) {
            console.warn('Malformed Microsoft response - attendee');
            console.warn(ex);
            console.table(JSON.stringify(event, null, 2));
            return false;
          }
        });

        if (foundAttendeeFromFoundResource) {
          return {
            attendee: foundAttendeeFromFoundResource,
            resource: r,
            location: location,
          };
        } else {
          // Pokud nenajdeme ani tak, zkusime uz absolutni hadani - nalezt ciste na zaklade attendes

          const foundFromOnlyAttendees = event.attendees
            .map((attendee) => {
              const foundResource = resources.find(
                (resource) =>
                  attendee.emailAddress?.address?.toUpperCase() === resource.email?.toUpperCase() ||
                  attendee.emailAddress?.address?.toUpperCase() === resource.username?.toUpperCase() ||
                  attendee.emailAddress?.address?.toUpperCase() ===
                    fromLocalized(resource.displayName, activeLanguage, defaultLanguage)?.toUpperCase() ||
                  attendee.emailAddress?.name?.toUpperCase() === resource.email?.toUpperCase() ||
                  attendee.emailAddress?.name?.toUpperCase() === resource.username?.toUpperCase() ||
                  attendee.emailAddress?.name?.toUpperCase() ===
                    fromLocalized(resource.displayName, activeLanguage, defaultLanguage)?.toUpperCase()
              );
              return {
                attendee: attendee,
                foundResource: foundResource,
              };
            })
            .find((pair) => pair.foundResource != null);

          if (foundFromOnlyAttendees) {
            return {
              attendee: foundFromOnlyAttendees.attendee,
              resource: foundFromOnlyAttendees.foundResource,
              location: location,
            };
          }

          return null;
        }
      } else {
        const foundFromOnlyAttendees = event.attendees
          .map((attendee) => {
            const isFoundResource =
              attendee.emailAddress?.address?.toUpperCase() === r.email?.toUpperCase() ||
              attendee.emailAddress?.address?.toUpperCase() === r.username?.toUpperCase() ||
              attendee.emailAddress?.address?.toUpperCase() ===
                fromLocalized(r.displayName, activeLanguage, defaultLanguage)?.toUpperCase() ||
              attendee.emailAddress?.name?.toUpperCase() === r.email?.toUpperCase() ||
              attendee.emailAddress?.name?.toUpperCase() === r.username?.toUpperCase() ||
              attendee.emailAddress?.name?.toUpperCase() ===
                fromLocalized(r.displayName, activeLanguage, defaultLanguage)?.toUpperCase();

            return {
              attendee: attendee,
              foundResource: isFoundResource ? r : null,
            };
          })
          .find((pair) => pair.foundResource != null);

        if (foundFromOnlyAttendees) {
          return {
            attendee: foundFromOnlyAttendees.attendee,
            resource: foundFromOnlyAttendees.foundResource,
            location: location,
          };
        } else {
          return null;
        }
      }
    })
    .filter((x) => x != null);

  const uniqueOnly = distinctBy(locationResourceStrategy, (x) => x.resource.id);
  finalArray.push(...locationAttendeeStategy);
  finalArray.push(...uniqueOnly.filter((x) => finalArray.find((y) => y.resource.id === x?.resource.id) == null));

  return finalArray;
}

export function findResourceFromEmailAddress(email: string, resources: AssistResourceBM[]) {
  return resources.find(
    (x) => x.email?.toUpperCase() === email?.toUpperCase() || x.username?.toUpperCase() === email?.toUpperCase()
  );
}

export function mergeAvailabilities(availability: CalendarAvailability[][]) {
  const mergedAvailability: CalendarAvailability[] = [];
  const legnth = availability && availability[0] ? availability[0].length : 0;
  for (let i = 0; i < legnth; i++) {
    const values = availability.map((x) => x[i]);
    mergedAvailability.push(getTotallAvailability(values));
  }
  return mergedAvailability;
}

export function getResourceAttendees(
  temporalAttendees: TemporalResourceAttendee[],
  calendarEvent: CalendarEvent,
  serverResponses: EventStatusResponse[]
) {
  let finalArray = temporalAttendees.map((rA) => {
    const response = findResourceAttendeeResponse(rA, calendarEvent.iCalUId, calendarEvent.id, serverResponses);

    const resourceStatusAndMetadata = getResourceAttendeeStatusAndConfirmationMetadata(calendarEvent, rA, response);

    const ianaTimeZone = getResourceTimeZone(rA.resource.workingHours);

    return {
      ...rA,
      status: resourceStatusAndMetadata[0],
      confirmationMetadata: resourceStatusAndMetadata[1],
      orderItemsCount: response?.orderItemsCount ?? 0,
      confirmedBy: response?.confirmedBy ?? null,
      confirmedWhen: response?.confirmedWhen ? new Date(response.confirmedWhen) : null,
      files: rA.resource.files,
      reservationsEnabled: rA.resource.reservationsEnabled,
      navigationEnabled: rA.resource.navigationEnabled,
      ianaTimeZone: ianaTimeZone,
      statusesByDay: response?.statusesByDay ?? [],
      confirmAvailable:
        resourceStatusAndMetadata[0] === ResourceAttendeeStatus.ConfirmNow &&
        resourceStatusAndMetadata[1].confirmByAssistAvailable,
    } satisfies ResourceAttendee;
  });

  return finalArray;
}

export function getAttendeeFromLocationAndAttendees(
  event: CalendarEvent,
  resources: AssistResourceBM[],
  activeLanguage: string,
  defaultLanguage: string
) {
  const resourcesInAttendees = event.attendees.filter((x) => x.resource === true);
  if (resourcesInAttendees.length === 1) {
    return resourcesInAttendees[0];
  }
  const foundStandardWay = event.attendees.find((x) => {
    try {
      return (
        event.locations.find(
          (location) =>
            (x.emailAddress.address != null &&
              x.emailAddress.address.toUpperCase() === location.locationUri?.toUpperCase()) ||
            (x.emailAddress.name != null &&
              x.emailAddress.name?.toUpperCase() === location.displayName?.toUpperCase()) ||
            (location.uniqueId != null && x.emailAddress.address.toUpperCase() === location.uniqueId?.toUpperCase()) ||
            (x.emailAddress.name != null &&
              x.emailAddress.name.toUpperCase() === location.locationUri?.toUpperCase()) ||
            (x.emailAddress.address != null &&
              x.emailAddress.address.toUpperCase() === location.locationUri?.toUpperCase()) ||
            (x.emailAddress.name != null &&
              x.emailAddress.name?.toUpperCase() === location.displayName?.toUpperCase()) ||
            (x.emailAddress.address != null &&
              location.displayName != null &&
              x.emailAddress.address?.toUpperCase() === location.displayName?.toUpperCase())
        ) != null
      );
    } catch (ex) {
      console.warn('Malformed Microsoft response - attendee');
      console.warn(ex);
      console.table(JSON.stringify(event, null, 2));
      return false;
    }
  });

  // Pokud se nenajde zkusime to hledat obecne v resourcech a naparovat.
  if (foundStandardWay == null) {
    // nejdrive najdeme location
    const foundResourceOfLocation = resources.find((r) => {
      return (
        event.locations.find(
          (location) =>
            r.username.toUpperCase() === location.locationUri?.toUpperCase() ||
            r.username.toUpperCase() === location.displayName?.toUpperCase() ||
            r.username.toUpperCase() === location.uniqueId?.toUpperCase() ||
            r.email.toUpperCase() === location.locationUri?.toUpperCase() ||
            r.email.toUpperCase() === location.displayName?.toUpperCase() ||
            r.email.toUpperCase() === location.uniqueId?.toUpperCase() ||
            fromLocalized(r.displayName, activeLanguage, defaultLanguage).toUpperCase() ===
              location.locationUri?.toUpperCase() ||
            fromLocalized(r.displayName, activeLanguage, defaultLanguage).toUpperCase() ===
              location.displayName?.toUpperCase() ||
            fromLocalized(r.displayName, activeLanguage, defaultLanguage).toUpperCase() ===
              location.uniqueId?.toUpperCase()
        ) != null
      );
    });

    if (foundResourceOfLocation) {
      // pokud jsme nalezli resource z lokace, muzeme zkusit najit attendeeho.
      const foundAttendeeFromFoundResource = event.attendees.find((x) => {
        try {
          return (
            x.emailAddress?.address.toUpperCase() == foundResourceOfLocation.email.toUpperCase() ||
            x.emailAddress?.address.toUpperCase() == foundResourceOfLocation.username.toUpperCase() ||
            x.emailAddress?.address.toUpperCase() ==
              fromLocalized(foundResourceOfLocation.displayName, activeLanguage, defaultLanguage).toUpperCase() ||
            x.emailAddress?.name.toUpperCase() == foundResourceOfLocation.email.toUpperCase() ||
            x.emailAddress?.name.toUpperCase() == foundResourceOfLocation.username.toUpperCase() ||
            x.emailAddress?.name.toUpperCase() ==
              fromLocalized(foundResourceOfLocation.displayName, activeLanguage, defaultLanguage).toUpperCase()
          );
        } catch (ex) {
          console.warn('Malformed Microsoft response - attendee');
          console.warn(ex);
          console.table(JSON.stringify(event, null, 2));
          return false;
        }
      });

      return foundAttendeeFromFoundResource;
    } else {
      // Pokud nenajdeme ani tak, zkusime uz absolutni hadani - nalezt ciste na zaklade attendes
      const foundFromOnlyAttendees = event.attendees.find((attendee) => {
        return (
          resources.find(
            (resource) =>
              attendee.emailAddress?.address?.toUpperCase() === resource.email?.toUpperCase() ||
              attendee.emailAddress?.address?.toUpperCase() === resource.username?.toUpperCase() ||
              attendee.emailAddress?.address?.toUpperCase() ===
                fromLocalized(resource.displayName, activeLanguage, defaultLanguage)?.toUpperCase() ||
              attendee.emailAddress?.name?.toUpperCase() === resource.email?.toUpperCase() ||
              attendee.emailAddress?.name?.toUpperCase() === resource.username?.toUpperCase() ||
              attendee.emailAddress?.name?.toUpperCase() ===
                fromLocalized(resource.displayName, activeLanguage, defaultLanguage)?.toUpperCase()
          ) != null
        );
      });

      if (foundFromOnlyAttendees) {
        console.warn('Event-resource pairing was semi-guessed on attandees');
        console.warn(event);
        return foundFromOnlyAttendees;
      } else {
        return null;
      }
    }
  }
  return foundStandardWay;
}

const randomColors = ['red', 'blue', 'green', 'orange', 'lightblue', 'yellow', 'gray', 'lightgray', 'cyan'];

function getRandomInt(max: number) {
  return Math.floor(Math.random() * Math.floor(max));
}

export function getAttendees(foundEventToConfirm: IdentifiedAssistEvent) {
  return foundEventToConfirm.attendees
    .filter((x) => x.type !== 'resource')
    .map((a) => ({
      name: a.emailAddress.name ?? a.emailAddress.address,
      email: a.emailAddress.address,
      status: a.status.response,
      initials:
        a.emailAddress.name != null
          ? a.emailAddress.name.trim().split(' ').length > 1
            ? a.emailAddress.name.trim().split(' ')[0][0] + a.emailAddress.name.trim().split(' ')[1][0]
            : '@'
          : '@',
      color: randomColors[getRandomInt(randomColors.length - 1)],
    }));
}

export function resourcesFullfilRequirement(
  resource: AssistResourceBM,
  requirement: Requirement,
  filter: FilterBase,
  licensingService: LicensingService,
  configService: ConfigService,
  translocoService: TranslocoService
): boolean {
  const anyAttendee = filter.attendees.length > 0;
  const filterByAttendeeFilter = !anyAttendee || licensingService.isPremiumResourceLike(resource);

  const attendeeCapacity =
    !anyAttendee ||
    resource.capacity === null ||
    resource.capacity >= filter.attendees.filter((x) => !x.remotely).length;

  const filtersFromConfig = configService.value.filters.filter(
    (x) => x.resourceTypeId === requirement.typeId && x.filterable === true
  );

  const filterFromResourceVisibilityRestrictionDescription =
    configService.value.resourceVisibilityRestriction == 1 ||
    resource.conditions.reservedTo == null ||
    resource.conditions.reservedTo == 'you';
  // Typ resourcu musi odpovidat
  const isTheSameType = resource.resourceTypeId === requirement.typeId;

  // Filtrovani podle kapacity
  const capacityRequire =
    requirement.capacity != null
      ? resource.capacity === null ||
        (resource.capacity >= requirement.capacity[0] && resource.capacity <= requirement.capacity[1])
      : true;

  // Filtrovani podle areas
  const areaRequire =
    requirement.areas && requirement.areas.length > 0
      ? resource.mapInfos.find(
          (workplaceMapinfo) =>
            requirement.areas!.find((requirementArea) => requirementArea === workplaceMapinfo.areaId) != null
        ) != null
      : true;

  // Filtrovani podle obecnych filtru

  const bools = (requirement.filters as any[]).map((f, filterIndex) => {
    if (Array.isArray(f) ? f.length > 0 : f != null) {
      const filterDefinition = filtersFromConfig[filterIndex];
      const dataTypeId = filterDefinition.dataTypeId;
      const properties = resource.properties.filter((x) => x.propertyId == filterDefinition.id);
      const value: any = null;
      switch (dataTypeId) {
        case 1: {
          return properties.length > 0 ? properties[0].valueBoolean === f : true;
        }
        case 2: {
          return properties.length > 0 ? properties[0].valueNumber >= f[0] && properties[0].valueNumber <= f[1] : true;
        }
        case 3: {
          return properties.length > 0
            ? properties[0].valueDecimal >= f[0] && properties[0].valueDecimal <= f[1]
            : true;
        }
        case 4: {
          return properties.length > 0
            ? remove(localized(properties[0].valueText, translocoService) ?? '')
                .toUpperCase()
                .includes(remove(f).toUpperCase())
            : false;
        }
        case 5: {
          const valueChoiceIds = properties.length > 0 ? properties[0].valueChoiceIds : [];
          const intersect = valueChoiceIds.filter((x) => f.find((e) => e.id === x) != null);
          return filterDefinition.matchAll === true ? intersect.length === f.length : intersect.length > 0;
        }
        default:
          return false;
      }
    } else {
      return true;
    }
  });

  const anyFalse = bools.find((x) => x === false) != null;
  return (
    filterFromResourceVisibilityRestrictionDescription &&
    isTheSameType &&
    capacityRequire &&
    areaRequire &&
    attendeeCapacity &&
    filterByAttendeeFilter &&
    !anyFalse
  );
}

export function getTextKeyAndClass(attendee: ResourceAttendee): [string, string, ResourceAttendeeStatus] {
  if (attendee.status === 'rejected') {
    return [translation.common.declined, 'declined', ResourceAttendeeStatus.Declined];
  }

  if (attendee.status === 'confirmed') {
    return [translation.common.confirmed, 'confirmed', ResourceAttendeeStatus.Confirmed];
  }

  if (attendee.status === 'need-confirmation') {
    return [translation.common.needConfirmation, 'need-confirmation', ResourceAttendeeStatus.NeedConfirmation];
  }

  if (attendee.status === 'confirm-now') {
    return [translation.common.confirmNow, 'confirm-now', ResourceAttendeeStatus.ConfirmNow];
  }

  if (attendee.status === 'unconfirmed') {
    return [translation.common.unconfirmed, 'unconfirmed', ResourceAttendeeStatus.Unconfirmed];
  }

  if (attendee.status === 'release-failed') {
    return [translation.common.releaseFailed, 'released', ResourceAttendeeStatus.ReleaseFailed];
  }

  if (attendee.status === 'released') {
    return [translation.common.released, 'released', ResourceAttendeeStatus.ReleasedForNonConfirmation];
  }

  if (attendee.status === 'accepted') {
    return [translation.common.accepted, 'accepted', ResourceAttendeeStatus.Accepted];
  }

  if (attendee.status === 'tentative') {
    return [translation.common.tentative, 'tentative', ResourceAttendeeStatus.Tentative];
  }

  return [translation.common.tentative, 'tentative', ResourceAttendeeStatus.Tentative];
}
